import { c as _c } from "react/compiler-runtime";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { BrandCardHeader } from "#/components/brand/BrandCardHeader";
import { Button } from "#/components/ui/button";
import { Card, CardContent } from "#/components/ui/card";
import { Input } from "#/components/ui/input";
import { LoadingButton } from "#/components/ui/loading-button";
import { StatusMessage } from "#/components/ui/status-message";
import { formatSchemaError, getFieldSchema, isSchemaError, parseSchema, validateField as validateSchemaField } from "#/lib/validation";
import { profileSchema } from "./EditProfileCard.schema";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function FormInput(t0) {
  const $ = _c(16);
  const {
    id,
    label,
    placeholder,
    value,
    onChange,
    error
  } = t0;
  let t1;
  if ($[0] !== id || $[1] !== label) {
    t1 = /*#__PURE__*/_jsx("label", {
      htmlFor: id,
      className: "text-sm font-medium",
      children: label
    });
    $[0] = id;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== onChange) {
    t2 = e => onChange(e.target.value);
    $[3] = onChange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== id || $[6] !== placeholder || $[7] !== t2 || $[8] !== value) {
    t3 = /*#__PURE__*/_jsx(Input, {
      id: id,
      name: id,
      placeholder: placeholder,
      value: value,
      onChange: t2,
      required: true,
      minLength: 2,
      maxLength: 50
    });
    $[5] = id;
    $[6] = placeholder;
    $[7] = t2;
    $[8] = value;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== error) {
    t4 = error && /*#__PURE__*/_jsx("p", {
      className: "text-sm text-red-500",
      children: error
    });
    $[10] = error;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t1 || $[13] !== t3 || $[14] !== t4) {
    t5 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-2",
      children: [t1, t3, t4]
    });
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
export default function EditProfileCard(t0) {
  const $ = _c(45);
  const {
    user,
    handleProfileUpdate,
    onBack,
    statusMessage,
    onStatusMessageAction,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== user) {
    t2 = () => {
      if (!user?.name) {
        return {
          firstName: "",
          lastName: ""
        };
      }
      const fullName = user.name.trim();
      const firstSpaceIndex = fullName.search(/\s+(?!-)/);
      if (firstSpaceIndex === -1) {
        return {
          firstName: fullName,
          lastName: ""
        };
      }
      const firstName = fullName.slice(0, firstSpaceIndex).trim();
      const lastName = fullName.slice(firstSpaceIndex + 1).trim();
      return {
        firstName,
        lastName
      };
    };
    $[0] = user;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [formData, setFormData] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [validationErrors, setValidationErrors] = useState(t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [errors, setErrors] = useState(t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    const validateField = (name, value) => {
      const schema = getFieldSchema(profileSchema, name);
      if (schema) {
        setValidationErrors(prev => ({
          ...prev,
          [name]: validateSchemaField(schema, value)
        }));
      }
    };
    t5 = (name_0, value_0) => {
      setFormData(prev_0 => ({
        ...prev_0,
        [name_0]: value_0
      }));
      validateField(name_0, value_0);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleFieldChange = t5;
  let t6;
  if ($[5] !== formData || $[6] !== handleProfileUpdate) {
    t6 = async e => {
      e.preventDefault();
      ;
      try {
        const validatedData = parseSchema(profileSchema, formData);
        const response = await handleProfileUpdate(validatedData.firstName, validatedData.lastName);
        if (!response.success) {
          setErrors({
            form: response.message
          });
        }
      } catch (t7) {
        const error = t7;
        if (isSchemaError(error)) {
          setErrors(formatSchemaError(error));
          setValidationErrors(Object.fromEntries(Object.entries(formatSchemaError(error)).map(_temp)));
        }
      }
    };
    $[5] = formData;
    $[6] = handleProfileUpdate;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleSubmit = t6;
  const hasValidationErrors = Object.values(validationErrors).some(_temp2);
  const isFormEmpty = !formData.firstName || !formData.lastName;
  if (statusMessage) {
    let t7;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = /*#__PURE__*/_jsx(BrandCardHeader, {
        title: "Redigera profil",
        subtitle: "Uppdatera dina personuppgifter h\xE4r."
      });
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    const t8 = statusMessage.variant === "confirmation" ? "p-6" : undefined;
    let t9;
    if ($[9] !== statusMessage.message || $[10] !== statusMessage.variant || $[11] !== t8) {
      t9 = /*#__PURE__*/_jsx(StatusMessage, {
        message: statusMessage.message,
        variant: statusMessage.variant,
        className: t8
      });
      $[9] = statusMessage.message;
      $[10] = statusMessage.variant;
      $[11] = t8;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    let t10;
    if ($[13] !== onStatusMessageAction) {
      t10 = /*#__PURE__*/_jsx(Button, {
        onClick: onStatusMessageAction,
        className: "w-full",
        children: "Forts\xE4tt"
      });
      $[13] = onStatusMessageAction;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    let t11;
    if ($[15] !== t10 || $[16] !== t9) {
      t11 = /*#__PURE__*/_jsxs(Card, {
        className: "m-auto w-screen overflow-y-auto rounded-none bg-card shadow-none sm:max-h-[calc(100vh-112px)] sm:max-w-md sm:rounded-lg sm:shadow-lg sm:shadow-zinc-500/10",
        children: [t7, /*#__PURE__*/_jsx(CardContent, {
          className: "p-6",
          children: /*#__PURE__*/_jsxs("div", {
            className: "space-y-4",
            children: [t9, t10]
          })
        })]
      });
      $[15] = t10;
      $[16] = t9;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    return t11;
  }
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = /*#__PURE__*/_jsx(BrandCardHeader, {
      title: "Redigera profil",
      subtitle: "Uppdatera dina personuppgifter h\xE4r."
    });
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = value_2 => handleFieldChange("firstName", value_2);
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== formData.firstName || $[21] !== validationErrors.firstName) {
    t9 = /*#__PURE__*/_jsx(FormInput, {
      id: "firstName",
      label: "F\xF6rnamn",
      placeholder: "Ange f\xF6rnamn",
      value: formData.firstName,
      onChange: t8,
      error: validationErrors.firstName
    });
    $[20] = formData.firstName;
    $[21] = validationErrors.firstName;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = value_3 => handleFieldChange("lastName", value_3);
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== formData.lastName || $[25] !== validationErrors.lastName) {
    t11 = /*#__PURE__*/_jsx(FormInput, {
      id: "lastName",
      label: "Efternamn",
      placeholder: "Ange efternamn",
      value: formData.lastName,
      onChange: t10,
      error: validationErrors.lastName
    });
    $[24] = formData.lastName;
    $[25] = validationErrors.lastName;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== errors.form) {
    t12 = errors.form && /*#__PURE__*/_jsx(StatusMessage, {
      message: errors.form,
      variant: "error"
    });
    $[27] = errors.form;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = isLoading || hasValidationErrors || isFormEmpty;
  let t14;
  if ($[29] !== isLoading || $[30] !== t13) {
    t14 = /*#__PURE__*/_jsx(LoadingButton, {
      type: "submit",
      className: "w-full",
      loading: isLoading,
      disabled: t13,
      children: "Spara \xE4ndringar"
    });
    $[29] = isLoading;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = /*#__PURE__*/_jsx(ArrowLeft, {
      className: "mr-2 h-4 w-4"
    });
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== isLoading || $[34] !== onBack) {
    t16 = /*#__PURE__*/_jsxs(Button, {
      type: "button",
      variant: "outline",
      className: "w-full",
      onClick: onBack,
      disabled: isLoading,
      children: [t15, "Tillbaka"]
    });
    $[33] = isLoading;
    $[34] = onBack;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t14 || $[37] !== t16) {
    t17 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-2",
      children: [t14, t16]
    });
    $[36] = t14;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== handleSubmit || $[40] !== t11 || $[41] !== t12 || $[42] !== t17 || $[43] !== t9) {
    t18 = /*#__PURE__*/_jsxs(Card, {
      className: "m-auto w-screen overflow-y-auto rounded-none bg-card shadow-none sm:max-h-[calc(100vh-112px)] sm:max-w-md sm:rounded-lg sm:shadow-lg sm:shadow-zinc-500/10",
      children: [t7, /*#__PURE__*/_jsx(CardContent, {
        className: "p-6",
        children: /*#__PURE__*/_jsxs("form", {
          onSubmit: handleSubmit,
          className: "space-y-4",
          children: [t9, t11, t12, t17]
        })
      })]
    });
    $[39] = handleSubmit;
    $[40] = t11;
    $[41] = t12;
    $[42] = t17;
    $[43] = t9;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  return t18;
}
function _temp2(error_0) {
  return error_0 !== undefined;
}
function _temp(t0) {
  const [key, value_1] = t0;
  return [key, value_1];
}